import React, { useState, useEffect } from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import store from 'store/Store';
import profileActions from 'actions/ProfileActions';

import {
    Box, Text, Link, Image, Container, Grid
} from 'components/ui';
import Loader from 'components/Loader';
import { colors } from 'style/config';
import PointsActivityCard from 'components/Content/BeautyInsider/BeautyInsiderModules/PointsActivityCard/PointsActivityCard';
import LoadingActivityCardWrapper from 'components/Content/BeautyInsider/BeautyInsiderModules/LoadingActivityCard/LoadingActivityCardWrapper';
import beautyInsiderPageBindings from 'analytics/bindingMethods/pages/beautyInsider/beautyInsiderPageBindings';

import LanguageLocaleUtils from 'utils/LanguageLocale';
import EN from './locales/AllPointsActivity_en_US';
import FR from './locales/AllPointsActivity_fr_CA';

const { getLocalisedDataFromResource } = LanguageLocaleUtils;
const getText = getLocalisedDataFromResource(EN, FR);

const { getAccountActivityPoints } = profileActions;
const NUMBER_OF_ACTIVITIES = 10;
const MAX_ACTIVITIES = 150;

const styles = {
    backlink: {
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        paddingTop: 24,
        paddingBottom: 32
    },
    title: {
        fontFamily: 'Helvetica Neue',
        fontSize: 32,
        fontStyle: 'normal',
        fontWeight: 700,
        lineHeight: '125%'
    }
};

const AllPointsActivity = ({
    records, error, user, totalPages, pageNumber
}) => {
    const [expandedGroupId, setExpandedGroupId] = useState(null);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [isRetrying, setIsRetrying] = useState(false);

    // Initial API call on mount - only if data doesn't exist in store
    useEffect(() => {
        if (user?.profileId && records === null) {
            store.dispatch(getAccountActivityPoints(user.profileId, NUMBER_OF_ACTIVITIES, 1));
        }
    }, [user?.profileId, records]);

    // Watch for Redux updates and clear loading state
    useEffect(() => {
        if (records) {
            setIsLoadingMore(false);
        }
    }, [records]);

    // Clear retry loading after minimum 1 second (regardless of success/failure)
    useEffect(() => {
        let timer;

        if (isRetrying) {
            timer = setTimeout(() => {
                setIsRetrying(false);
            }, 1000);
        }

        return () => clearTimeout(timer);
    }, [isRetrying]);

    const handleCardToggle = (groupId, record) => {
        // If clicking the same card, collapse it; otherwise expand the new one
        const isExpanding = expandedGroupId !== groupId;
        setExpandedGroupId(isExpanding ? groupId : null);

        // Fire analytics when expanding
        if (isExpanding) {
            beautyInsiderPageBindings.firePointsActivityExpandAnalytics(record);
        }
    };

    const showMoreActivities = e => {
        e.preventDefault();

        if (user?.profileId && pageNumber) {
            const nextPage = pageNumber + 1;
            setIsLoadingMore(true);
            store.dispatch(getAccountActivityPoints(user.profileId, NUMBER_OF_ACTIVITIES, nextPage));
        }
    };

    const handleRetry = e => {
        e.preventDefault();

        if (user?.profileId) {
            setIsRetrying(true);
            store.dispatch(getAccountActivityPoints(user.profileId, NUMBER_OF_ACTIVITIES, 1));
        }
    };

    // Calculate derived state from store records
    const hasReachedMaxActivities = records && records.length >= MAX_ACTIVITIES;
    const activitiesToDisplay = hasReachedMaxActivities ? records.slice(0, MAX_ACTIVITIES) : records;
    const shouldShowMore = pageNumber < totalPages;

    // Render content zone (following ShortPointsActivity pattern)
    const renderContent = () => {
        // Retrying state
        if (isRetrying) {
            return <LoadingActivityCardWrapper numberOfCards={10} />;
        }

        // Error state (check first)
        if (error) {
            return (
                <Box marginTop={4}>
                    <Text>{getText('errorLoadingPoints')} </Text>
                    <Link
                        onClick={handleRetry}
                        color='blue'
                    >
                        {getText('retry')}
                    </Link>
                </Box>
            );
        }

        // Loading state: initial load only (records hasn't arrived yet)
        if (records === null) {
            return <LoadingActivityCardWrapper numberOfCards={10} />;
        }

        // Empty state: no activities after loading
        if (!records || records.length === 0) {
            return (
                <Box>
                    <Image
                        display='block'
                        size={[48, 120]}
                        marginBottom={[4, 4]}
                        src='/img/ufe/no-points.svg'
                    />
                    <Text
                        is='p'
                        marginBottom={3}
                    >
                        {getText('noPoints')}
                    </Text>
                    <Text
                        is='p'
                        color='gray'
                        fontSize='sm'
                    >
                        {getText('doNotSeePoints')}
                    </Text>
                </Box>
            );
        }

        // Render activities from local state
        return (
            <Box
                paddingY={4}
                marginTop={4}
                borderBottom={`1px solid ${colors.lightGray}`}
            >
                {activitiesToDisplay.map((record, index) => (
                    <PointsActivityCard
                        key={record.groupId || index}
                        record={record}
                        onToggle={() => handleCardToggle(record.groupId, record)}
                        isExpanded={expandedGroupId === record.groupId}
                    />
                ))}
            </Box>
        );
    };

    return (
        <Container height='100%'>
            <Link
                href='/profile/BeautyInsider'
                css={styles.backlink}
                arrowDirection='left'
                arrowPosition='before'
                children={getText('backLink')}
            />
            <Grid
                columns='minmax(auto, 610px)'
                gap={4}
            >
                <Text
                    is='h1'
                    css={styles.title}
                    marginTop={4}
                >
                    {getText('title')}
                </Text>

                {renderContent()}

                {shouldShowMore && !hasReachedMaxActivities && (
                    <>
                        {isLoadingMore && (
                            <Loader
                                isShown={true}
                                isInline={true}
                                data-at={Sephora.debug.dataAt('activity-loader')}
                            />
                        )}
                        {!isLoadingMore && (
                            <Link
                                marginX='auto'
                                arrowDirection='down'
                                onClick={showMoreActivities}
                            >
                                {getText('viewMoreTransactionsText')}
                            </Link>
                        )}
                    </>
                )}
            </Grid>
        </Container>
    );
};

export default wrapFunctionalComponent(AllPointsActivity, 'AllPointsActivity');
